import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next/pages';
import { type ParseKeys } from 'i18next';
import { isFullListing, isRangeDto, type UserAccountExclusiveListing, type Listing, type SearchListing } from '@pkgs/api';

import { getCountFromValue } from '@/utils/translationLabels';
import { formatRangeWithUnit, formatValueWithUnit } from '@/utils/formatPropertyDetails';
import { TRANSLATION_NAMESPACE } from '@/i18n/config';
import { type KeyInfoData } from '@/components/property-expose/KeyInfo/KeyInfo.data';

import { useSettings } from './useSettings';

export type KeyInfoItem = {
  formattedValue?: string;
  formattedWithLabel?: string;
  label: string;
  id: string;
  labelKey: ParseKeys<'property_expose'>;
  icon?: ReactNode;
};

export const useKeyInfo = ({
  listing,
  items,
}: {
  listing: Listing | SearchListing | UserAccountExclusiveListing;
  items: KeyInfoData[];
}): KeyInfoItem[] => {
  const { t } = useTranslation(TRANSLATION_NAMESPACE.property_expose);
  const { language } = useSettings();

  const { measurementSystem } = useSettings();

  const transformItem = useCallback(
    (data: KeyInfoData): KeyInfoItem | undefined => {
      const { labelKey, value, formatValue, formatWithLabel, id, icon, formatOptions } = data;
      const { isSurface, shouldShowRoundedValue } = formatOptions ?? {};

      if (!value || (isRangeDto(value) && !value.min)) return undefined;

      if (isFullListing(listing) && listing.isDevelopmentProject && isRangeDto(value)) {
        const formattedValue = formatValue
          ? formatValue(value)
          : formatRangeWithUnit({
              values: value,
              measurementSystem,
              language: language,
              isSurface,
            });

        return {
          id,
          labelKey,
          icon,
          label: t(labelKey, { count: value.max }),
          formattedValue,
          formattedWithLabel: formatWithLabel
            ? formatWithLabel(formattedValue, labelKey, t)
            : `${formattedValue} ${t(labelKey, {
                count: value.max,
              })}`,
        };
      }

      const formattedValue = formatValue
        ? formatValue(value)
        : formatValueWithUnit({
            value: isRangeDto(value) ? value.min! : Number(value),
            measurementSystem,
            language: language,
            isSurface,
            shouldShowRoundedValue,
          });

      return {
        id,
        labelKey,
        icon,
        label: t(labelKey, { count: getCountFromValue(value) }),
        formattedValue,
        formattedWithLabel: formatWithLabel
          ? formatWithLabel(formattedValue, labelKey, t)
          : `${formattedValue} ${t(labelKey, { count: getCountFromValue(value) })}`,
      };
    },
    [listing, measurementSystem, language, t],
  );

  const keyInfoItems = useMemo<KeyInfoItem[]>(
    () => items.map((item) => transformItem(item)).filter(Boolean) as KeyInfoItem[],
    [items, transformItem],
  );

  return keyInfoItems;
};
